import { TopicTreeSortBy } from '@/features/Taxonomy/context/TaxonomyContext';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';

import { arrayToMap, stringSort } from '../../../utils/util';

const getTopicWithoutTaxonomy = (
  topicTreeMap: Record<string, TopicTreeT>,
  topicAlias: string,
): Topic2TreeItemT<TOPIC_2_TYPE> | null => {
  const taxonomyAliases = Object.keys(topicTreeMap);
  for (const taxonomyAlias of taxonomyAliases) {
    const topicTree = topicTreeMap[taxonomyAlias];
    const topic = topicTree.topicMap[topicAlias];
    if (topic) return topic;
  }
  return null;
};

const getTaxonomyTopic = (
  topicTreeMap: Record<string, TopicTreeT>,
  taxonomyAlias: string,
  topicAlias: string,
) => {
  const topicTree = topicTreeMap[taxonomyAlias];
  if (!topicTree) throw new Error('Topic tree is not exist for this taxonomy');
  const topic = topicTree.topicMap[topicAlias];
  if (!topic) throw new Error('Topic is not exist');
  return topic;
};

const getTopicFromTopicTree = (tree: TopicTreeT, topicAlias: string) => {
  const topic = tree.topicMap[topicAlias];
  if (!topic) throw new Error('Topic is not exist');
  return topic;
};

const getTaxonomyTopicTree = (
  topicTreeMap: Record<string, TopicTreeT>,
  taxonomyAlias: string,
) => {
  const tree = topicTreeMap[taxonomyAlias];
  if (!tree) throw new Error('TopicTree is not exist for this taxonomy');
  return tree;
};

const topicSortFunc = (
  topics: Topic2TreeItemT<TOPIC_2_TYPE>[],
  sortBy: TopicTreeSortBy,
  order: OrderStatusT,
) => {
  return topics.sort((a, b) => {
    let origin, compare: Topic2TreeItemT<TOPIC_2_TYPE>;
    switch (order) {
      case 'desc': {
        origin = a;
        compare = b;
        break;
      }
      case 'asc': {
        origin = b;
        compare = a;
        break;
      }
      case 'unset': {
        return 0;
      }
      default:
        throw new Error('INVALID ORDER');
    }
    switch (sortBy) {
      case TopicTreeSortBy.TITLE: {
        return stringSort(origin.name, compare.name);
      }
      default:
        return 0;
    }
  });
};

const getTopicPath = (topic: {
  taxonomy_name?: string;
  taxonomy_id?: string;
  parent_topic_lv1?: Topic2ParentInfo;
  parent_topic_lv2?: Topic2ParentInfo;
  parent_topic_lv3?: Topic2ParentInfo;
}): (ValueLabelT & { isTaxonomy: boolean })[] => {
  const paths: (ValueLabelT & { isTaxonomy: boolean })[] = [];
  const taxonomyName = topic.taxonomy_name && topic.taxonomy_name;
  topic.taxonomy_id &&
    taxonomyName &&
    paths.push({
      value: topic.taxonomy_id,
      label: taxonomyName,
      isTaxonomy: true,
    });
  const lv1Parent = topic.parent_topic_lv1;
  if (lv1Parent) {
    paths.push({
      value: lv1Parent.parent_id,
      label: lv1Parent.parent_name,
      isTaxonomy: false,
    });
  }
  const lv2Parent = topic.parent_topic_lv2;
  if (lv2Parent) {
    paths.push({
      value: lv2Parent.parent_id,
      label: lv2Parent.parent_name,
      isTaxonomy: false,
    });
  }
  const lv3Parent = topic.parent_topic_lv3;
  if (lv3Parent) {
    paths.push({
      value: lv3Parent.parent_id,
      label: lv3Parent.parent_name,
      isTaxonomy: false,
    });
  }
  return paths;
};

const canCreateChildTopic = (level?: number) => {
  // if topic is not exist, it means taxonomy level
  if (!level) return true;
  switch (level) {
    case 1:
    case 2:
    case 3:
      return true;
    case 4:
      return false;
    default:
      return false;
  }
};

const buildTopicTree = (topics: TopicRes) => {
  const roots = topics
    .filter((topic) => topic.level === 1)
    .map((topic) => topic.alias);
  const topicMap: Record<string, Topic2Base<TOPIC_2_TYPE>> = arrayToMap(
    topics,
    'alias',
  );
  const mapper = (alias: string): FilterOptionItemT => {
    const topic = topicMap[alias];
    return {
      columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
      filterKey: topic.alias,
      filterName: topic.name,
      filterOptionNode: topic.name,
      filterList: topic.children?.map(mapper),
    };
  };
  return roots.map(mapper);
};

const getAncestors = (
  topic: Topic2TreeItemT<TOPIC_2_TYPE>,
  topicTreeMap: Record<string, TopicTreeT>,
): Topic2TreeItemT<TOPIC_2_TYPE>[] => {
  let ancestors: Topic2TreeItemT<TOPIC_2_TYPE>[] = [];
  let current = topic;

  let parentIds: string[] = [];
  switch (current.level) {
    case 4:
      parentIds.push(current.parent_topic_lv3?.parent_id || '');
      parentIds.push(current.parent_topic_lv2?.parent_id || '');
      parentIds.push(current.parent_topic_lv1?.parent_id || '');
      break;
    case 3:
      parentIds.push(current.parent_topic_lv2?.parent_id || '');
      parentIds.push(current.parent_topic_lv1?.parent_id || '');
      break;
    case 2:
      parentIds.push(current.parent_topic_lv1?.parent_id || '');
      break;
    default:
      break;
  }

  parentIds.forEach((id) => {
    const parent = taxonomyUtils.getTopicWithoutTaxonomy(topicTreeMap, id);
    if (parent) ancestors.push(parent);
  });

  return ancestors;
};

const getUniqueParentTopics = (
  startTopic: Topic2TreeItemT<TOPIC_2_TYPE>,
  targetTopic: Topic2TreeItemT<TOPIC_2_TYPE>,
  topicTreeMap: Record<string, TopicTreeT>,
): Topic2TreeItemT<TOPIC_2_TYPE>[] => {
  if (!startTopic || !targetTopic) return [];

  const startAncestors = getAncestors(startTopic, topicTreeMap).filter(
    (t) => t.alias !== targetTopic.alias,
  );
  const targetAncestors = getAncestors(targetTopic, topicTreeMap).filter(
    (t) => t.alias !== startTopic.alias,
  );

  function findCommonElementsWithComparator<T>(
    arr1: T[],
    arr2: T[],
    comparator: (a: T, b: T) => boolean,
  ): T[] {
    return arr1.filter((item1) =>
      arr2.some((item2) => comparator(item1, item2)),
    );
  }

  const allTopics = [...new Set([...startAncestors, ...targetAncestors])];

  const commonTopics = findCommonElementsWithComparator(
    startAncestors,
    targetAncestors,
    (a, b) => a.alias === b.alias,
  );

  const uniqueTopics = allTopics.filter(
    (topic) =>
      !commonTopics.some((commonTopic) => commonTopic.alias === topic.alias),
  );

  return uniqueTopics;
};

const validateDropTopic = (params: {
  startTopicAlias?: string;
  isTaxonomy: boolean;
  targetTopicAlias: string;
  taxonomyAlias: string;
  topicTreeMap: Record<string, TopicTreeT>;
}) => {
  const {
    startTopicAlias,
    isTaxonomy,
    targetTopicAlias,
    taxonomyAlias,
    topicTreeMap,
  } = params;

  if (startTopicAlias === targetTopicAlias) return false;
  const validateAcestors = (
    ancestors: Topic2TreeItemT<TOPIC_2_TYPE>[],
  ): boolean => {
    for (const topic of ancestors) {
      if (topic.category === TOPIC_2_TYPE.RULE) {
        return false;
      }
    }
    return true;
  };

  const targetTopic = taxonomyUtils.getTaxonomyTopic(
    topicTreeMap,
    taxonomyAlias,
    targetTopicAlias,
  );

  if (targetTopic.category === TOPIC_2_TYPE.RULE) return false;
  if (isTaxonomy) {
    const ancestors = taxonomyUtils.getAncestors(targetTopic, topicTreeMap);
    return validateAcestors(ancestors);
  }

  if (!startTopicAlias) throw new Error('Start topic alias is required');

  const startTopic = taxonomyUtils.getTaxonomyTopic(
    topicTreeMap,
    taxonomyAlias,
    startTopicAlias,
  );

  if (startTopic) {
    if (
      startTopic?.parent_topic_lv1?.parent_id === targetTopic.alias ||
      startTopic?.parent_topic_lv2?.parent_id === targetTopic.alias ||
      startTopic?.parent_topic_lv3?.parent_id === targetTopic.alias
    ) {
      if (startTopic.category === TOPIC_2_TYPE.RULE) return false;
    }

    if (
      targetTopic.parent_topic_lv1?.parent_id === startTopic.alias ||
      targetTopic.parent_topic_lv2?.parent_id === startTopic.alias ||
      targetTopic.parent_topic_lv3?.parent_id === startTopic.alias
    ) {
      const uniqueParents = taxonomyUtils.getUniqueParentTopics(
        startTopic,
        targetTopic,
        topicTreeMap,
      );
      const result = validateAcestors(uniqueParents);
      if (result) {
        return 'START_TOPIC_IS_PARENT_OF_TARGET_TOPIC';
      }
      return false;
    }

    const uniqueParents = taxonomyUtils.getUniqueParentTopics(
      startTopic,
      targetTopic,
      topicTreeMap,
    );
    return validateAcestors(uniqueParents);
  }
  return false;
};

const taxonomyUtils = {
  getTopicWithoutTaxonomy,
  getTaxonomyTopic,
  getTopicFromTopicTree,
  getTaxonomyTopicTree,
  topicSortFunc,
  getTopicPath,
  canCreateChildTopic,
  buildTopicTree,
  getUniqueParentTopics,
  getAncestors,
  validateDropTopic,
};

export default taxonomyUtils;
